import { DeleteFilled, DoubleRightOutlined, CheckCircleOutlined } from "@ant-design/icons"
import { Tooltip, Popconfirm, Button, Table, Tag } from "antd"
import { ColumnsType } from "antd/es/table"
import { FC, useMemo } from "react"
import { Link } from "react-router-dom"
import { EResultTagTL, FlexRow } from "../../../components/commons-ts/common"
import { Equipment, Impact } from "../../../generated/proto-ts/main"
import { Translated } from "../../../utils/translated"
import { pbUUIDToUuid, COLOR_BG_ROW_DELETED } from "../../../utils/utils"
import {
  _calculatePerformanceFactor,
  getEquipmentResultTL,
  getImpactResultTL,
} from "../../../calculus/calculus_TL"
import { useSyncronizationContext } from "../../../providers/syncronizationProvider"

export const ZoneImpactsTableTL: FC<{
  impacts: Impact[] | null
  equipmentImpactorWeight: number | null
  currentImpactUUID?: string | null
  onDeleteImpact?: (impact: Impact | null) => void
}> = ({ impacts, currentImpactUUID, equipmentImpactorWeight, onDeleteImpact }) => {
  const { isAdmin } = useSyncronizationContext()

  const filteredImpacts = useMemo(() => {
    if (impacts === null) {
      return null
    }
    return impacts.filter((impact) => impact.deleted_at === 0)
  }, [impacts])

  const columns: ColumnsType<Impact> = useMemo(() => {
    if (impacts === null) {
      return []
    }
    let impactIds = impacts.map((impact) => pbUUIDToUuid(impact.uuid))
    let _columns: ColumnsType<Impact> = [
      {
        title: "#",
        render: (_, impact) => {
          return impactIds.indexOf(pbUUIDToUuid(impact.uuid)) + 1
        },
        // sorter: (a, b) => (a.id ?? 0) - (b.id ?? 0),
      },
      {
        key: "height",
        title: (
          <Tooltip overlay={<Translated keyEn="Free Fall Height, m" />}>
            <div>
              <Translated keyEn="Height, m" />
            </div>
          </Tooltip>
        ),
        render: (_, impact) => {
          if (impact.impact_ffh === 0) {
            return null
          }
          return <span>{impact.impact_ffh.toFixed(2)}</span>
        },
        sorter: (a, b) => a.impact_ffh - b.impact_ffh,
      },
      {
        key: "gmax",
        title: <Translated keyEn="Gmax" />,
        render: (_, impact) => {
          if (impact.impact_gmax === 0) {
            return null
          }
          return <span>{impact.impact_gmax.toFixed(1)}</span>
        },
        sorter: (a, b) => a.impact_gmax - b.impact_gmax,
      },
      {
        key: "rebounce_height",
        title: (
          <Tooltip overlay={<Translated keyEn="Rebounce Height, mm" />}>
            <span>
              <Translated keyEn="Rebounce, mm" />
            </span>
          </Tooltip>
        ),
        render: (_, impact) => {
          if (impact.impact_rebounce_height === 0) {
            return null
          }
          let valueMM = 1e3 * impact.impact_rebounce_height
          return <span>{valueMM.toFixed(0)}</span>
        },
        sorter: (a, b) => a.impact_rebounce_height - b.impact_rebounce_height,
      },
      {
        key: "performance_factor",
        title: (
          <Tooltip overlay={<Translated keyEn="Performance Factor" />}>
            <span>
              <Translated keyEn="PF" />
            </span>
          </Tooltip>
        ),
        render: (_, impact) => {
          if (impact.impact_rebounce_height === 0 || impact.impact_gmax === 0) {
            return null
          }
          let performanceFactor = _calculatePerformanceFactor(
            impact.impact_rebounce_height,
            impact.impact_gmax,
          )
          return <span>{Math.ceil(performanceFactor)}</span>
        },
        sorter: (a, b) => {
          let performanceFactorA = _calculatePerformanceFactor(
            a.impact_rebounce_height,
            a.impact_gmax,
          )
          let performanceFactorB = _calculatePerformanceFactor(
            b.impact_rebounce_height,
            b.impact_gmax,
          )
          return performanceFactorA - performanceFactorB
        },
      },
      {
        key: "timestamp",
        title: <Translated keyEn="Date" />,
        render: (_, impact) => {
          if (impact.created_at === 0) {
            return null
          }
          return new Date(impact.created_at).toLocaleString()
        },
        sorter: (a, b) => a.created_at - b.created_at,
        defaultSortOrder: "descend",
      },
      {
        key: "result",
        title: <Translated keyEn="Result" />,
        render: (_, impact) => {
          let result = getImpactResultTL(impact)
          if (result === undefined) {
            return <Tag>N/A</Tag>
          }
          if (result === null) {
            return <Tag>N/A</Tag>
          }
          return <EResultTagTL result={result.result} />
        },
      },
      {
        title: "",
        width: "2rem",
        render: (_, impact) => {
          return (
            <FlexRow style={{ gap: 0 }}>
              {onDeleteImpact !== undefined && (
                <Popconfirm
                  title={
                    <>
                      <Translated keyEn="Are you sure you want to delete this impact" />?
                    </>
                  }
                  onConfirm={() => {
                    onDeleteImpact(impact)
                  }}
                >
                  <Button danger type="link" size="small" icon={<DeleteFilled />} />
                </Popconfirm>
              )}
              {currentImpactUUID === undefined ||
              currentImpactUUID !== pbUUIDToUuid(impact.uuid) ? (
                <Link to={`/impacts/${pbUUIDToUuid(impact.uuid)}`}>
                  <Button type="link" size="small">
                    <DoubleRightOutlined />
                  </Button>
                </Link>
              ) : (
                <FlexRow
                  style={{
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  <CheckCircleOutlined />
                </FlexRow>
              )}
            </FlexRow>
          )
        },
      },
    ]
    return _columns
  }, [filteredImpacts, currentImpactUUID, equipmentImpactorWeight])

  return (
    <Table
      dataSource={filteredImpacts ?? []}
      loading={filteredImpacts === null}
      columns={columns}
      pagination={false}
      rowKey={(e) => pbUUIDToUuid(e.uuid)}
      size="small"
      scroll={{ x: true }}
      bordered
      style={{
        width: "100%",
      }}
      onRow={(record) => {
        if (record.deleted_at === 0) {
          return {}
        }
        if (isAdmin) {
          return {
            style: {
              backgroundColor: COLOR_BG_ROW_DELETED,
            },
          }
        } else {
          return {
            style: {
              display: "none",
            },
          }
        }
      }}
      footer={() => {
        return (
          <i>
            <div>
              <Translated keyEn="Table of impacts: EN ISO 23659 - TRAMPOLINES" />
            </div>
          </i>
        )
      }}
    />
  )
}

export const ZoneImpactsTableTL_Print: FC<{
  impacts: Impact[] | null
  equipmentImpactorWeight: number | null
}> = ({ impacts, equipmentImpactorWeight }) => {
  const filteredImpacts = useMemo(() => {
    if (impacts === null) {
      return null
    }
    return impacts.filter((impact) => impact.deleted_at === 0)
  }, [impacts])

  const columns: ColumnsType<Impact> = useMemo(() => {
    if (impacts === null) {
      return []
    }
    let impactIds = impacts.map((impact) => pbUUIDToUuid(impact.uuid))
    let _columns: ColumnsType<Impact> = [
      {
        title: "#",
        render: (_, impact) => {
          return impactIds.indexOf(pbUUIDToUuid(impact.uuid)) + 1
        },
      },
      {
        key: "height",
        title: <Translated keyEn="Height, m" />,
        render: (_, impact) => {
          if (impact.impact_ffh === 0) {
            return null
          }
          return <span>{impact.impact_ffh.toFixed(2)}</span>
        },
      },
      {
        key: "gmax",
        title: <Translated keyEn="Gmax" />,
        render: (_, impact) => {
          if (impact.impact_gmax === 0) {
            return null
          }
          return <span>{impact.impact_gmax.toFixed(1)}</span>
        },
      },
      {
        key: "rebounce_height",
        title: <Translated keyEn="Rebounce, mm" />,
        render: (_, impact) => {
          if (impact.impact_rebounce_height === 0) {
            return null
          }
          let valueMM = 1e3 * impact.impact_rebounce_height
          return <span>{valueMM.toFixed(0)}</span>
        },
      },
      {
        key: "performance_factor",
        title: <Translated keyEn="PF" />,
        render: (_, impact) => {
          if (impact.impact_rebounce_height === 0 || impact.impact_gmax === 0) {
            return null
          }
          let performanceFactor = _calculatePerformanceFactor(
            impact.impact_rebounce_height,
            impact.impact_gmax,
          )
          return <span>{Math.ceil(performanceFactor)}</span>
        },
      },
      {
        key: "timestamp",
        title: <Translated keyEn="Date" />,
        render: (_, impact) => {
          if (impact.created_at === 0) {
            return null
          }
          return new Date(impact.created_at).toLocaleString()
        },
      },
    ]
    return _columns
  }, [filteredImpacts, equipmentImpactorWeight])

  return (
    <Table
      dataSource={filteredImpacts ?? []}
      loading={filteredImpacts === null}
      columns={columns}
      pagination={false}
      rowKey={(e) => pbUUIDToUuid(e.uuid)}
      size="small"
      scroll={{ x: true }}
      bordered
      style={{
        width: "100%",
      }}
      footer={() => {
        return (
          <i>
            <div>
              <Translated keyEn="Table of impacts: EN ISO 23659 - TRAMPOLINES" />
            </div>
          </i>
        )
      }}
    />
  )
}
